import type { $Enums } from '@prisma/client';

import type { SearchDescriptor } from '../descriptor';
import type { Predicate } from '../predicate';
import { always, and, eq, eqOpt, or } from '../predicate';

/** Both indexers write `businessEntity.roles` verbatim, so the indexed value is the full Prisma enum. */
export type BusinessEntitySearchRole = $Enums.BusinessEntityRole;

/** The subset the pickers filter on; a rename in the Prisma enum breaks this instead of drifting. */
export type SelectableBusinessEntityRole = Extract<
  BusinessEntitySearchRole,
  'VENDOR' | 'CUSTOMER' | 'CARRIER' | 'GROWER'
>;

export interface BusinessEntityDoc {
  readonly objectID: string;
  readonly businessEntityId: number;
  readonly organizationId: number;
  readonly name: string;
  readonly roles: readonly BusinessEntitySearchRole[];
  readonly areRolesEmpty: boolean;
  readonly disabled: boolean;
}

export const BE_DESCRIPTOR: SearchDescriptor<BusinessEntityDoc> = {
  entity: 'businessEntity',
  indexName: { algolia: 'businessEntities', typesense: 'businessEntities' },
  searchable: ['name'],
  filterable: ['businessEntityId', 'roles', 'areRolesEmpty', 'disabled', 'organizationId'],
  defaults: { hitsPerPage: 20 },
  algolia: {
    searchableAttributes: [{ path: ['name'], mode: 'unordered' }],
    attributesForFaceting: [
      { path: ['areRolesEmpty'] },
      { path: ['businessEntityId'] },
      { path: ['disabled'] },
      { path: ['organizationId'] },
      { path: ['roles'] },
    ],
  },
  ranking: { typesense: { queryBy: 'name' } },
  tenantField: 'organizationId',
};

export const BE_SELECTABLE_AS = (role: SelectableBusinessEntityRole): Predicate<BusinessEntityDoc> =>
  and([or([eq('areRolesEmpty', true), eq('roles', role)]), eq('disabled', false)]);

/**
 * Entities offered for ANY of `roles`, plus entities with no roles at all. Unlike
 * {@link BE_SELECTABLE_AS} it takes the full role enum and does not hide disabled entities.
 * An empty `roles` still offers the role-less entities and nothing else.
 */
export const BE_OFFERED_FOR_ROLES = (roles: readonly BusinessEntitySearchRole[]): Predicate<BusinessEntityDoc> =>
  or([eq('areRolesEmpty', true), ...roles.map((role) => eq<BusinessEntityDoc>('roles', role))]);

export const BE_STRICTLY = (role: SelectableBusinessEntityRole): Predicate<BusinessEntityDoc> =>
  and([eq('roles', role), eq('disabled', false)]);

/**
 * Business entities an expense row may be charged to. `payeeScope` narrows the picker to
 * the bill's payee; omitting it leaves every enabled entity selectable.
 */
export const BE_FOR_EXPENSE = (payeeScope?: Predicate<BusinessEntityDoc>): Predicate<BusinessEntityDoc> =>
  and([payeeScope ?? always<BusinessEntityDoc>(), eq('disabled', false)]);

/** The payee an expense is billed to. An unresolved payee matches nothing — never everything. */
export const BE_EXPENSE_PAYEE = (businessEntityId: number | null | undefined): Predicate<BusinessEntityDoc> =>
  eqOpt('businessEntityId', businessEntityId);
